import { service } from '@ember/service'
import Component from '@glimmer/component'
import { isHTMLSafe, htmlSafe } from '@ember/template'
import { colors } from '@colors'
import { tracked } from '@glimmer/tracking'
import { DataProcessor } from '@blakeelearning/data-tables/utils/data-processor'
import { t, type IntlService } from 'ember-intl'
import type Student from 'teacher-ui-client/models/student'
import { on } from '@ember/modifier'
import { fn } from '@ember/helper'
import SortColumnIcon from 'teacher-ui-client/components/sort-column-icon'
import { eq } from 'ember-truth-helpers'
import ChartInfoTooltip from 'teacher-ui-client/components/giraffe/chart-info-tooltip'
import gradeName from 'teacher-ui-client/helpers/grade-name'
import formatValue from 'teacher-ui-client/helpers/format-value'
import BaseHighcharterComponent from 'teacher-ui-client/components/base/highcharter'
import { differenceWith } from 'lodash'
import type { UIScopeName } from 'teacher-ui-client/reporting/ui-scope/route'
import { Legend, TD, TH, type LegendsItem } from 'teacher-ui-client/components/giraffe/primitives/history-tables'
import type RouterService from '@ember/routing/router-service'
import type Owner from '@ember/owner'
import { hasId } from 'teacher-ui-client/utils/type-guards'

export type DrivingTestsHistoryMathsData = {
  driving_test_grade: number
  students: MathsStudentData[]
}

export type DrivingTestsHistoryReData = {
  students: ReStudentData[]
}

type MathsStudentData = {
  student_id: number
  completed: number
  total_attempts: number
  data: number
  number: number
  operations: number
  geometry: number
  measurement: number
  patterns: number
}

type ReStudentData = {
  student_id: number
  completed: number
  total_attempts: number
  content_words: number
  letters_and_sounds: number
  sight_words: number
}

interface HistoryDataViewModel {
  student_id: string
  id: string
  firstName: string
  lastName: string
  teacherName: string
  grade_position: number
  completed: number | null
  totalAttempts: number | null
  measurement?: number
  data?: number
  geometry?: number
  patterns?: number
  operations?: number
  number?: number
  sight_words?: number
  letters_and_sounds?: number
  content_words?: number
}

interface Args {
  drivingTestsHistoryData: DrivingTestsHistoryMathsData | DrivingTestsHistoryReData
  students: Student[]
  product: 're' | 'maths'
  uiScope: UIScopeName
  title?: string
  subco?: boolean
  tooltip: string
  studentRouteName: string
  drivingTestsGrade?: number
}

interface Signature {
  Element: HTMLDivElement
  Args: Args
}

type CategoryKey =
  | 'number'
  | 'operations'
  | 'patterns'
  | 'measurement'
  | 'geometry'
  | 'data'
  | 'sight_words'
  | 'letters_and_sounds'
  | 'content_words'

interface ChartArgs {
  categories: CategoryKey[]
  data: HistoryDataViewModel
  showYAxisLabels: boolean
  maxValue: number
  product: 're' | 'maths'
}

function isReData(studentData?: MathsStudentData | ReStudentData): studentData is ReStudentData {
  if (!studentData) return false
  return 'sight_words' in studentData && 'letters_and_sounds' in studentData && 'content_words' in studentData
}

function isMathData(studentData?: MathsStudentData | ReStudentData): studentData is MathsStudentData {
  if (!studentData) return false
  return (
    'number' in studentData &&
    'operations' in studentData &&
    'patterns' in studentData &&
    'measurement' in studentData &&
    'geometry' in studentData &&
    'data' in studentData
  )
}

const buildLexileChartOptions = function (chartOptions: ChartArgs): Highcharts.Options {
  const yAxis: Highcharts.YAxisOptions = {
    title: {
      text: '',
    },
    opposite: true,
    max: chartOptions.maxValue,
    minorTicks: false,
    labels: {
      enabled: chartOptions.showYAxisLabels,
    },
    offset: -10,
  }

  const series = chartOptions.categories.map((category) => {
    return {
      type: 'bar' as const,
      name: category,
      data: [chartOptions.data[category] ?? 0],
    }
  })

  return {
    series,
    chart: {
      type: 'bar',
      // Make some room for the yaxis labels
      margin: chartOptions.showYAxisLabels ? [20, 0, 0, 0] : [0, 0, 0, 0],
      backgroundColor: 'transparent',
      // always disable animation for this chart, as they're all separate charts and don't animate together
      animation: false,
    },
    tooltip: {
      enabled: true,
      formatter: function (this: Highcharts.Point) {
        return `<b>${this.y}`
      },
      backgroundColor: colors.dustyBlack500,
      borderColor: colors.dustyBlack500,
      style: {
        color: colors.white,
      },
    },
    colors:
      chartOptions.product === 're'
        ? [colors.purplyPink300, colors.forestGreen300, colors.oceanyBlue300]
        : [
            colors.wlBlue250,
            colors.watermelonyRed300,
            colors.yolkyYellow300,
            colors.purplyPink300,
            colors.forestGreen300,
            colors.oceanyBlue300,
          ],
    credits: {
      enabled: false,
    },
    title: {
      text: '',
    },
    xAxis: {
      maxPadding: 0,
      minPadding: 0,
      labels: {
        enabled: false,
      },
      lineWidth: 0,
      tickWidth: 0,
      gridLineWidth: 0,
      minorGridLineWidth: 0,
    },
    legend: { enabled: false },
    yAxis,
    plotOptions: {
      bar: {
        animation: false,
        borderWidth: 0,
        colorByPoint: false,
        stacking: 'normal',
        pointWidth: 40,
      },
    },
  }
}

export class DrivingTestsHistory extends Component<Signature> {
  @service router!: RouterService

  @tracked sortingPriorityQueue = []

  @service intl!: IntlService

  @tracked processor: DataProcessor<HistoryDataViewModel>

  constructor(owner: Owner, args: Args) {
    super(owner, args)

    this.processor = new DataProcessor(this)
  }

  get categories(): CategoryKey[] {
    if (this.args.product === 're') {
      return ['sight_words', 'letters_and_sounds', 'content_words']
    } else {
      return ['number', 'operations', 'patterns', 'geometry', 'data', 'measurement']
    }
  }

  get data() {
    return this.tableRows
  }

  get tableRows() {
    const stats = this.args.drivingTestsHistoryData.students
    return this.args.students.filter(hasId).map((student) => {
      const statRow = stats?.find((stat) => String(stat.student_id) === student.id)
      let categoryData
      if (isMathData(statRow)) {
        categoryData = {
          number: statRow?.number ?? null,
          operations: statRow?.operations ?? null,
          patterns: statRow?.patterns ?? null,
          geometry: statRow?.geometry ?? null,
          data: statRow?.data ?? null,
          measurement: statRow?.measurement ?? null,
        }
      }
      if (isReData(statRow)) {
        categoryData = {
          sight_words: statRow?.sight_words ?? null,
          letters_and_sounds: statRow?.letters_and_sounds ?? null,
          content_words: statRow?.content_words ?? null,
        }
      }
      return {
        firstName: student.firstName,
        lastName: student.lastName,
        teacherName:
          this.args.product === 'maths' ? student.mathsTeacherNames.join(', ') : student.readingTeacherNames.join(', '),
        grade_position: student.gradePosition,
        id: student.id,
        student_id: student.id,
        completed: statRow?.completed ?? null,
        totalAttempts: statRow?.total_attempts ?? null,
        ...categoryData,
      }
    })
  }

  get legendCategories(): LegendsItem[] {
    const { intl } = this
    const { product } = this.args

    return this.categories.map((category) => {
      switch (`${product}.${category}`) {
        case 'maths.number':
          return { color: 'bg-oceany-blue-300', label: intl.t('driving_tests.maths.categories.number') }
        case 'maths.operations':
          return { color: 'bg-forest-green-300', label: intl.t('driving_tests.maths.categories.operations') }
        case 'maths.patterns':
          return { color: 'bg-purply-pink-300', label: intl.t('driving_tests.maths.categories.patterns') }
        case 'maths.measurement':
          return { color: 'bg-wl-blue-250', label: intl.t('driving_tests.maths.categories.measurement') }
        case 'maths.geometry':
          return { color: 'bg-yolky-yellow-300', label: intl.t('driving_tests.maths.categories.geometry') }
        case 'maths.data':
          return { color: 'bg-watermelony-red-300', label: intl.t('driving_tests.maths.categories.data') }
        case 're.sight_words':
          return { color: 'bg-oceany-blue-300', label: intl.t('driving_tests.re.categories.sight_words') }
        case 're.letters_and_sounds':
          return { color: 'bg-forest-green-300', label: intl.t('driving_tests.re.categories.letters_and_sounds') }
        case 're.content_words':
          return { color: 'bg-purply-pink-300', label: intl.t('driving_tests.re.categories.content_words') }
        default:
          return { color: '', label: '' }
      }
    })
  }

  get amendedTooltip() {
    const { tooltip } = this.args
    const studentData: Record<string, unknown>[] = this.args.drivingTestsHistoryData.students ?? []
    const missingStudentsCount = differenceWith(
      this.args.students,
      studentData,
      (student, historyStudent) => student.id === String(historyStudent.student_id),
    ).length

    if (missingStudentsCount) {
      const wasSafe = isHTMLSafe(tooltip)
      const newTooltip = this.args.tooltip + this.intl.t('reporting.missingStudentsCount', { missingStudentsCount })
      return wasSafe ? htmlSafe(newTooltip) : newTooltip
    }
    return tooltip
  }

  get maxDrivingTest() {
    const { drivingTestsHistoryData } = this.args

    const testsCompleted = drivingTestsHistoryData.students.map((studentData) => {
      if (isReData(studentData)) {
        return studentData.sight_words + studentData.letters_and_sounds + studentData.content_words
      } else if (isMathData(studentData)) {
        return (
          studentData.number +
          studentData.operations +
          studentData.patterns +
          studentData.measurement +
          studentData.geometry +
          studentData.data
        )
      }
      return 0
    })

    const highestTestsCompleted = testsCompleted ? Math.max(...testsCompleted) : 10
    return Math.ceil(highestTestsCompleted / 5) * 5 // give a little extra space on the chart (nearest multiple of 5)
  }

  get rowsWithChartOptions() {
    return this.processor.transformedData.map((row, index) => {
      return {
        ...row,
        chartOptions: buildLexileChartOptions({
          categories: this.categories.slice().reverse(),
          data: row,
          showYAxisLabels: index === 0,
          maxValue: this.maxDrivingTest,
          product: this.args.product,
        }),
      }
    })
  }

  get showChart() {
    return this.args.uiScope !== 'school'
  }

  transitionToStudentRoute = (studentId: string) => {
    const { drivingTestsGrade } = this.args
    if (typeof drivingTestsGrade === 'number') {
      void this.router.transitionTo(this.args.studentRouteName, studentId, { queryParams: { drivingTestsGrade } })
    } else {
      void this.router.transitionTo(this.args.studentRouteName, studentId)
    }
  }

  <template>
    <div
      data-test-giraffe-driving-tests-history
      class="border-dusty-black-50 shadow-dusty-black-100 relative border shadow-md"
      ...attributes
    >
      <ChartInfoTooltip data-test-driving-tests-tooltip @tooltipContent={{this.amendedTooltip}} />
      <div class="overflow-x-auto print:overflow-visible">
        <table class="w-full min-w-6xl overflow-hidden rounded-sm text-sm">
          <thead>
            <tr class="bg-oceany-blue-300 divide-x divide-white font-semibold text-white">
              <TH class="w-36" role="button" {{on "click" (fn this.processor.tableActions.sortByValue "firstName")}}>
                <div class="flex items-center justify-between">
                  {{t "reporting.dashboardTables.firstName" htmlSafe=true}}
                  <SortColumnIcon @sortingPriorityQueue={{this.sortingPriorityQueue}} @columnName="firstName" />
                </div>
              </TH>
              <TH class="w-36" role="button" {{on "click" (fn this.processor.tableActions.sortByValue "lastName")}}>
                <div class="flex items-center justify-between">
                  {{t "reporting.dashboardTables.lastName" htmlSafe=true}}
                  <SortColumnIcon @sortingPriorityQueue={{this.sortingPriorityQueue}} @columnName="lastName" />
                </div>
              </TH>
              {{#if @subco}}
                <TH
                  class="w-48"
                  role="button"
                  {{on "click" (fn this.processor.tableActions.sortByValue "teacherName")}}
                >
                  <div class="flex items-center justify-between">
                    {{t "reporting.dashboardTables.teacher"}}
                    <SortColumnIcon @sortingPriorityQueue={{this.sortingPriorityQueue}} @columnName="teacherName" />
                  </div>
                </TH>
                <TH
                  class="w-24"
                  role="button"
                  {{on "click" (fn this.processor.tableActions.sortByNumber "grade_position")}}
                >
                  <div class="flex items-center justify-between">
                    {{t "grade.label"}}
                    <SortColumnIcon @sortingPriorityQueue={{this.sortingPriorityQueue}} @columnName="grade_position" />
                  </div>
                </TH>
              {{/if}}
              <TH
                class="w-36 text-wrap"
                role="button"
                {{on "click" (fn this.processor.tableActions.sortByNumber "completed")}}
              >
                <div class="flex items-center justify-between">
                  {{t "reporting.charts.courseProgress.history.testsCompleted" htmlSafe=true}}
                  <SortColumnIcon @sortingPriorityQueue={{this.sortingPriorityQueue}} @columnName="completed" />
                </div>
              </TH>
              <TH
                class="w-36"
                role="button"
                {{on "click" (fn this.processor.tableActions.sortByNumber "totalAttempts")}}
              >
                <div class="flex items-center justify-between font-bold">
                  {{t "reporting.charts.courseProgress.history.testAttempts" htmlSafe=true}}
                  <SortColumnIcon @sortingPriorityQueue={{this.sortingPriorityQueue}} @columnName="totalAttempts" />
                </div>
              </TH>
              {{#if (eq @product "maths")}}
                <th class="print-only-table-cell">
                  {{t "reporting.charts.courseProgress.history.measurement"}}
                </th>
                <th class="print-only-table-cell">
                  {{t "reporting.charts.courseProgress.history.data"}}
                </th>
                <th class="print-only-table-cell">
                  {{t "reporting.charts.courseProgress.history.geometry"}}
                </th>
                <th class="print-only-table-cell">
                  {{t "reporting.charts.courseProgress.history.patternsAndFractions"}}
                </th>
                <th class="print-only-table-cell">
                  {{t "reporting.charts.courseProgress.history.operations"}}
                </th>
                <th class="print-only-table-cell">
                  {{t "reporting.charts.courseProgress.history.number"}}
                </th>
              {{else}}
                <th class="print-only-table-cell">
                  {{t "reporting.charts.courseProgress.history.sightWords"}}
                </th>
                <th class="print-only-table-cell">
                  {{t "reporting.charts.courseProgress.history.lettersAndSounds"}}
                </th>
                <th class="print-only-table-cell">
                  {{t "reporting.charts.courseProgress.history.contentWords"}}
                </th>
              {{/if}}
              {{#if this.showChart}}
                <TH
                  class="{{if @subco 'w-1/3' 'w-1/2'}}
                    border-l-dusty-black-100 bg-white px-0! whitespace-nowrap text-black print:hidden"
                >
                  <Legend
                    data-test-driving-tests-legend
                    @title={{t "reporting.chartTitles.drivingTestsHistory"}}
                    @categories={{this.legendCategories}}
                  />
                </TH>
              {{/if}}
            </tr>
          </thead>
          <tbody class="divide-dusty-black-100 divide-y-2 print:border-t print:border-black">
            {{#each this.rowsWithChartOptions as |row index|}}
              {{! template-lint-disable no-invalid-interactive }}
              <tr
                data-test-driving-tests-student-row={{index}}
                class="hover:cursor-pointer"
                {{on "click" (fn this.transitionToStudentRoute row.student_id)}}
              >
                <TD class="text-oceany-blue-300">
                  {{row.firstName}}
                </TD>
                <TD class="text-oceany-blue-300">
                  {{row.lastName}}
                </TD>
                {{#if @subco}}
                  <TD>
                    <div class="{{if this.showChart 'max-h-20 overflow-auto'}}">{{row.teacherName}}</div>
                  </TD>
                  <TD class="text-center">
                    {{gradeName row.grade_position}}
                  </TD>
                {{/if}}
                <TD data-test-completed class="text-center">
                  {{formatValue row.completed}}
                </TD>
                <TD class="text-center">
                  {{formatValue row.totalAttempts}}
                </TD>
                {{#if (eq @product "maths")}}
                  <td class="print-only-table-cell text-center">
                    {{formatValue row.measurement}}
                  </td>
                  <td class="print-only-table-cell text-center">
                    {{formatValue row.data}}
                  </td>
                  <td class="print-only-table-cell text-center">
                    {{formatValue row.geometry}}
                  </td>
                  <td class="print-only-table-cell text-center">
                    {{formatValue row.patterns}}
                  </td>
                  <td class="print-only-table-cell text-center">
                    {{formatValue row.operations}}
                  </td>
                  <td class="print-only-table-cell text-center">
                    {{formatValue row.number}}
                  </td>
                {{else}}
                  <td class="print-only-table-cell text-center">
                    {{formatValue row.sight_words}}
                  </td>
                  <td class="print-only-table-cell text-center">
                    {{formatValue row.letters_and_sounds}}
                  </td>
                  <td class="print-only-table-cell text-center">
                    {{formatValue row.content_words}}
                  </td>
                {{/if}}
                {{#if this.showChart}}
                  <TD class="border-dusty-black-100 border-l px-0! print:hidden">
                    <BaseHighcharterComponent
                      data-test-driving-tests-highchart-row
                      class="mr-3 h-full"
                      @options={{row.chartOptions}}
                    />
                  </TD>
                {{/if}}
              </tr>
            {{/each}}
          </tbody>
        </table>
      </div>
    </div>
  </template>
}

export default DrivingTestsHistory
